// API Configuration
export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Generic API request function
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('auth_token');
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }

  return data;
}

// Auth API
export const authAPI = {
  register: (email: string, password: string, name?: string) =>
    apiRequest<{ token: string; user: User; message: string }>('/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    }),

  login: (email: string, password: string) =>
    apiRequest<{ token: string; user: User }>('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  refresh: () =>
    apiRequest<{ token: string; user: User }>('/v1/auth/refresh', {
      method: 'POST',
    }),

  verifyEmail: (token: string) =>
    apiRequest<{ message: string; user: User }>('/v1/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  resendVerification: (email: string) =>
    apiRequest<{ message: string; already_verified?: boolean }>('/v1/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  forgotPassword: (email: string) =>
    apiRequest<{ message: string }>('/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  verifyResetToken: (token: string) =>
    apiRequest<{ valid: boolean }>(`/v1/auth/verify-reset-token/${token}`),

  resetPassword: (token: string, password: string) =>
    apiRequest<{ message: string }>('/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
};

// Widgets API
export const widgetsAPI = {
  list: () => apiRequest<{ widgets: Widget[] }>('/v1/widgets'),
  
  get: (id: string) => apiRequest<{ widget: Widget }>(`/v1/widgets/${id}`),
  
  create: (data: CreateWidgetData) =>
    apiRequest<{ widget: Widget }>('/v1/widgets', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<Widget>) =>
    apiRequest<{ widget: Widget }>(`/v1/widgets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    apiRequest<{ success: boolean }>(`/v1/widgets/${id}`, {
      method: 'DELETE',
    }),
  
  getEmbedCode: (id: string) =>
    apiRequest<{ embed_code: string; widget_id: string }>(`/v1/widgets/${id}/embed-code`),
};

// API Keys API
export const apiKeysAPI = {
  list: () => apiRequest<{ api_keys: ApiKey[] }>('/v1/api-keys'),
  
  create: (name: string, apiKey: string) =>
    apiRequest<{ api_key: ApiKey }>('/v1/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name, api_key: apiKey }),
    }),
  
  delete: (id: string) =>
    apiRequest<{ success: boolean }>(`/v1/api-keys/${id}`, {
      method: 'DELETE',
    }),
  
  validate: (id: string) =>
    apiRequest<{ is_valid: boolean }>(`/v1/api-keys/${id}/validate`, {
      method: 'POST',
    }),
  
  listAssistants: (id: string) =>
    apiRequest<{ assistants: Assistant[] }>(`/v1/api-keys/${id}/assistants`),

  createAssistant: (id: string, data: CreateAssistantData) =>
    apiRequest<{ assistant: Assistant }>(`/v1/api-keys/${id}/assistants`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

export interface CreateAssistantData {
  name: string;
  instructions?: string;
  model?: string;
  description?: string;
}

// Domains API
export const domainsAPI = {
  list: (widgetId: string) =>
    apiRequest<{ domains: Domain[] }>(`/v1/domains/${widgetId}`),
  
  add: (widgetId: string, domain: string) =>
    apiRequest<{ domain: Domain }>(`/v1/domains/${widgetId}`, {
      method: 'POST',
      body: JSON.stringify({ domain }),
    }),
  
  remove: (widgetId: string, domainId: string) =>
    apiRequest<{ success: boolean }>(`/v1/domains/${widgetId}/${domainId}`, {
      method: 'DELETE',
    }),
};

// Analytics API
export const analyticsAPI = {
  overview: (days = 7) =>
    apiRequest<AnalyticsOverview>(`/v1/analytics/overview?days=${days}`),

  widget: (widgetId: string, days = 7) =>
    apiRequest<WidgetAnalytics>(`/v1/analytics/${widgetId}?days=${days}`),

  daily: (widgetId: string, days = 30) =>
    apiRequest<{ daily: DailyAnalytics[] }>(`/v1/analytics/${widgetId}/daily?days=${days}`),
};

// User API
export const userAPI = {
  getProfile: () =>
    apiRequest<{ user: User; stats: { widgets: number; api_keys: number } }>('/v1/user/profile'),

  updateProfile: (data: { name?: string; email?: string }) =>
    apiRequest<{ user: User }>('/v1/user/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiRequest<{ message: string }>('/v1/user/password', {
      method: 'PUT',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),
};

// Billing API
export const billingAPI = {
  getPlans: () =>
    apiRequest<{ plans: PlanConfig[] }>('/v1/billing/plans'),

  getSubscription: () =>
    apiRequest<{
      subscription: {
        plan: string;
        trial_ends_at: string | null;
        stripe_customer_id: string | null;
        stripe_subscription_id: string | null;
        status: string;
        plan_config: PlanConfig | null;
        current_period_end: string | null;
        cancel_at_period_end: boolean;
      };
      usage: {
        messages_sent: number;
        api_calls: number;
        month: string;
      };
    }>('/v1/billing/subscription'),

  createCheckout: (plan: string, interval: 'monthly' | 'yearly') =>
    apiRequest<{ checkout_url: string; session_id: string }>('/v1/billing/create-checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, interval }),
    }),

  createPortal: () =>
    apiRequest<{ portal_url: string }>('/v1/billing/create-portal', {
      method: 'POST',
    }),

  cancelSubscription: () =>
    apiRequest<{ message: string }>('/v1/billing/cancel', {
      method: 'POST',
    }),

  getUsage: () =>
    apiRequest<{
      usage: {
        widgets: { used: number; limit: number };
        api_keys: { used: number; limit: number };
        messages: { used: number; limit: number };
        month: string;
      };
    }>('/v1/billing/usage'),
};

// Admin API
export const adminAPI = {
  getStats: () =>
    apiRequest<{
      stats: {
        users: {
          total: number;
          verified: number;
          by_plan: Array<{ plan: string; count: number }>;
          new_today: number;
          new_this_month: number;
        };
        subscriptions: { active: number };
        widgets: { total: number; active: number };
        usage: { messages_this_month: number };
      };
    }>('/v1/admin/stats'),

  getRevenueStats: () =>
    apiRequest<{
      revenue: {
        mrr: number;
        mrr_formatted: string;
        subscriptions_by_plan: Array<{ plan: string; count: number }>;
        monthly_signups: Array<{ month: string; count: number }>;
      };
    }>('/v1/admin/revenue'),

  listUsers: (params?: { page?: number; limit?: number; search?: string; plan?: string }) =>
    apiRequest<{
      users: AdminUser[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/v1/admin/users?${new URLSearchParams(params as any).toString()}`),

  getUser: (id: string) =>
    apiRequest<{
      user: AdminUser;
      widgets: Array<{ id: string; name: string; is_active: boolean; created_at: string }>;
      api_keys: Array<{ id: string; name: string; key_preview: string; is_valid: boolean; created_at: string }>;
      usage: { messages_sent: number; api_calls: number; month: string };
    }>(`/v1/admin/users/${id}`),

  updateUser: (id: string, data: Partial<AdminUser>) =>
    apiRequest<{ user: AdminUser }>(`/v1/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteUser: (id: string) =>
    apiRequest<{ success: boolean }>(`/v1/admin/users/${id}`, {
      method: 'DELETE',
    }),

  listPlans: () =>
    apiRequest<{ plans: PlanConfig[] }>('/v1/admin/plans'),

  updatePlan: (id: string, data: Partial<PlanConfig>) =>
    apiRequest<{ plan: PlanConfig }>(`/v1/admin/plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getStripeStatus: () =>
    apiRequest<{
      status: {
        secret_key_configured: boolean;
        webhook_secret_configured: boolean;
        secret_key_source: 'environment' | 'database' | 'none';
        webhook_secret_source: 'environment' | 'database' | 'none';
        api_working: boolean;
      };
    }>('/v1/admin/stripe-status'),

  listSecrets: () =>
    apiRequest<{
      secrets: Array<{
        secret_name: string;
        description: string | null;
        updated_by: string | null;
        created_at: string;
        updated_at: string;
      }>;
    }>('/v1/admin/secrets'),

  setSecret: (name: string, value: string, description?: string) =>
    apiRequest<{ success: boolean; secret_name: string }>(`/v1/admin/secrets/${name}`, {
      method: 'PUT',
      body: JSON.stringify({ value, description }),
    }),

  deleteSecret: (name: string) =>
    apiRequest<{ success: boolean }>(`/v1/admin/secrets/${name}`, {
      method: 'DELETE',
    }),
};

// Types
export interface User {
  id: string;
  email: string;
  name: string | null;
  plan: 'starter' | 'pro' | 'enterprise' | 'free' | 'trial';
  trial_ends_at?: string | null;
  email_verified?: boolean;
  role?: 'user' | 'admin';
  stripe_customer_id?: string | null;
  subscription_status?: string;
}

export interface AdminUser extends User {
  widget_count?: number;
  api_key_count?: number;
  created_at: string;
  updated_at: string;
}

export interface PlanConfig {
  id: string;
  plan_name: string;
  display_name: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  max_widgets: number;
  max_domains_per_widget: number;
  max_messages_per_month: number;
  max_api_keys: number;
  features: string[];
  is_active: boolean;
  sort_order: number;
  trial_days: number;
}

export interface ApiKey {
  id: string;
  name: string;
  key_preview: string;
  is_valid: boolean;
  last_used_at: string | null;
  created_at: string;
}

export interface Assistant {
  id: string;
  name: string | null;
  description: string | null;
  model: string;
  created_at: string;
}

export interface ThemeConfig {
  embedMode: 'popup' | 'inline';
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  userBubbleColor: string;
  userBubbleTextColor: string;
  assistantBubbleColor: string;
  assistantBubbleTextColor: string;
  headerBackgroundColor: string;
  headerTextColor: string;
  buttonColor: string;
  buttonIconColor: string;
  buttonPosition: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  buttonSize: 'small' | 'medium' | 'large';
  buttonOffsetX: number;
  buttonOffsetY: number;
  windowWidth: number;
  windowHeight: number;
  borderRadius: number;
  inlineWidth: string;
  inlineHeight: string;
  fontFamily: string;
  fontSize: number;
}

export interface Widget {
  id: string;
  name: string;
  description: string | null;
  assistant_id: string;
  api_key_id: string;
  is_active: boolean;
  theme_config: ThemeConfig;
  welcome_message: string;
  placeholder_text: string;
  show_branding: boolean;
  rate_limit_messages: number;
  rate_limit_sessions: number;
  allowed_domains: Domain[];
  domain_count?: number;
  created_at: string;
  updated_at: string;
}

export interface Domain {
  id: string;
  widget_id: string;
  domain: string;
  is_active: boolean;
  created_at: string;
}

export interface CreateWidgetData {
  name: string;
  api_key_id: string;
  assistant_id: string;
  description?: string;
  theme_config?: Partial<ThemeConfig>;
  welcome_message?: string;
  placeholder_text?: string;
  allowed_domains: string[];
}

export interface AnalyticsOverview {
  period: { days: number; start_date: string; end_date: string };
  totals: {
    total_events: number;
    page_views: number;
    widget_opens: number;
    messages_sent: number;
    conversations: number;
    unique_sessions: number;
    unique_domains: number;
  };
  widgets: Array<{
    id: string;
    name: string;
    total_events: number;
    messages_sent: number;
    unique_sessions: number;
  }>;
}

export interface WidgetAnalytics {
  widget: { id: string; name: string };
  period: { days: number; start_date: string; end_date: string };
  totals: {
    total_events: number;
    page_views: number;
    widget_opens: number;
    messages_sent: number;
    conversations: number;
    unique_sessions: number;
  };
  by_domain: Array<{ domain: string; total_events: number; messages_sent: number; unique_sessions: number }>;
  by_country: Array<{ country: string; total_events: number; unique_sessions: number }>;
  hourly_distribution: Array<{ hour: string; events: number; messages: number }>;
}

export interface DailyAnalytics {
  date: string;
  page_views: number;
  widget_opens: number;
  messages_sent: number;
  conversations: number;
  unique_sessions: number;
}

export const DEFAULT_THEME: ThemeConfig = {
  embedMode: 'popup',
  primaryColor: '#3b82f6',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
  userBubbleColor: '#3b82f6',
  userBubbleTextColor: '#ffffff',
  assistantBubbleColor: '#f3f4f6',
  assistantBubbleTextColor: '#1f2937',
  headerBackgroundColor: '#3b82f6',
  headerTextColor: '#ffffff',
  buttonColor: '#3b82f6',
  buttonIconColor: '#ffffff',
  buttonPosition: 'bottom-right',
  buttonSize: 'medium',
  buttonOffsetX: 20,
  buttonOffsetY: 20,
  windowWidth: 380,
  windowHeight: 600,
  borderRadius: 12,
  inlineWidth: '100%',
  inlineHeight: '600px',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontSize: 14,
};
